
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Calculator, Clock, Truck, Shield, DollarSign } from 'lucide-react';
import { mockServicePrices } from '@/api/mockData';
import type { ServicePrice } from '@/types';

interface PriceEstimatorProps {
  weight: number;
  service: string;
  province?: string;
  declaredValue?: number;
  onPriceCalculated?: (price: number) => void;
}

const PriceEstimator: React.FC<PriceEstimatorProps> = ({ 
  weight, 
  service, 
  province,
  declaredValue = 0,
  onPriceCalculated 
}) => {
  const [estimatedPrices, setEstimatedPrices] = useState<Array<ServicePrice & { 
    serviceFee: number;
    shippingFee: number; 
    insuranceFee: number;
    totalFee: number;
    totalCollection: number;
  }>>([]);

  useEffect(() => {
    if (weight > 0) {
      const prices = mockServicePrices.map(servicePrice => {
        const distanceMultiplier = province ? servicePrice.distanceMultiplier : 1;
        const serviceFee = Math.round(servicePrice.basePrice * distanceMultiplier);
        // Chuyển đổi từ gram sang kilogram cho tính toán phí vận chuyển
        const weightInKg = weight / 1000;
        const shippingFee = Math.round(weightInKg * servicePrice.weightPrice * distanceMultiplier);
        const insuranceFee = declaredValue > 0 ? Math.round(declaredValue * 0.005) : 0; // 0.5% of declared value
        const totalFee = serviceFee + shippingFee + insuranceFee;
        const totalCollection = totalFee; // Can add COD fee here if needed

        if (service === servicePrice.service && onPriceCalculated) {
          onPriceCalculated(totalCollection);
        }

        return {
          ...servicePrice,
          serviceFee,
          shippingFee,
          insuranceFee,
          totalFee,
          totalCollection
        };
      });
      setEstimatedPrices(prices);
    }
  }, [weight, service, province, declaredValue, onPriceCalculated]);

  if (weight <= 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <Calculator className="h-4 w-4" />
            Bảng giá ước tính
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-sm">
            Nhập cân nặng để xem giá ước tính
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm">
          <Calculator className="h-4 w-4" />
          Ước tính chi phí ({weight}g)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {estimatedPrices.map((priceInfo) => (
          <div 
            key={priceInfo.id}
            className={`p-4 rounded-lg border transition-colors ${
              service === priceInfo.service 
                ? 'border-primary bg-primary/5' 
                : 'border-border hover:bg-muted/50'
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Truck className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">{priceInfo.service}</span>
                {service === priceInfo.service && (
                  <Badge variant="secondary" className="text-xs">Đã chọn</Badge>
                )}
              </div>
              <div className="flex items-center gap-1 text-muted-foreground text-sm">
                <Clock className="h-3 w-3" />
                <span>{priceInfo.estimatedTime}</span>
              </div>
            </div>
            
            {service === priceInfo.service && (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="flex items-center gap-1">
                    <DollarSign className="h-3 w-3" />
                    Phí dịch vụ:
                  </span>
                  <span>{priceInfo.serviceFee.toLocaleString()}đ</span>
                </div>
                <div className="flex justify-between">
                  <span className="flex items-center gap-1">
                    <Truck className="h-3 w-3" />
                    Phí vận chuyển:
                  </span>
                  <span>{priceInfo.shippingFee.toLocaleString()}đ</span>
                </div>
                <div className="flex justify-between">
                  <span className="flex items-center gap-1">
                    <Shield className="h-3 w-3" />
                    Phí bảo hiểm:
                  </span>
                  <span>{priceInfo.insuranceFee.toLocaleString()}đ</span>
                </div>
                <Separator />
                <div className="flex justify-between font-medium">
                  <span>Tổng phí:</span>
                  <span>{priceInfo.totalFee.toLocaleString()}đ</span>
                </div>
                <div className="flex justify-between font-bold text-primary">
                  <span>Tổng thu:</span>
                  <span>{priceInfo.totalCollection.toLocaleString()}đ</span>
                </div>
              </div>
            )}
            
            {service !== priceInfo.service && (
              <div className="text-right">
                <span className="font-bold text-primary text-lg">
                  {priceInfo.totalCollection.toLocaleString()}đ
                </span>
              </div>
            )}
          </div>
        ))}
        <div className="pt-2 border-t text-xs text-muted-foreground">
          * Giá cuối cùng có thể thay đổi tùy theo khoảng cách, khối lượng, dịch vụ và các yếu tố khác
        </div>
      </CardContent>
    </Card>
  );
};

export default PriceEstimator;
